import { getMenuPermissionsApi } from '../service/menu';
import { getUserInfoApi } from '../service/user';
import { getMembershipCurrentApi } from '../service/membership';
import { globalStore } from './globalStore';
import type { Menu } from '../service/menu/types';
import { normalizeMenuPath } from './menuPathNormalize';

/**
 * 将新菜单格式转换为现有代码期望的格式
 */
export const convertNewMenuToOldFormat = (newMenu: Menu.NewMenu): Menu.Menu => {
  let component = newMenu.component ? newMenu.component.replace(/^\/+/, '') : '';
  const rawPath = newMenu.path || '';
  const path = normalizeMenuPath(rawPath);
  if (rawPath.trim() === "/voice-design" && component === "pages/voicePublic/Index") {
    component = "pages/voiceDesign/Index";
  }

  let icon = newMenu.icon || '';
  if (icon && icon.startsWith('fa:')) {
    icon = icon.replace('fa:', 'fa-');
  }

  return {
    id: newMenu.id,
    parentId: newMenu.parentId,
    name: newMenu.name,
    path,
    hidden: !newMenu.visible || newMenu.status === 1,
    component,
    type: newMenu.type,
    alwaysShow: newMenu.alwaysShow,
    sort: newMenu.sort,
    meta: {
      title: newMenu.name,
      icon,
      noCache: !newMenu.keepAlive,
      link: null,
      perms: newMenu.permission ?? undefined,
      action: newMenu.action ?? undefined,
      displayPosition: newMenu.displayPosition,
    },
    children: newMenu.children && newMenu.children.length > 0
      ? newMenu.children.map(child => convertNewMenuToOldFormat(child)).sort((a, b) => (a.sort || 0) - (b.sort || 0))
      : []
  };
};

/**
 * 初始化服务 - 在登录后获取菜单和用户信息
 */
export class InitService {
  /**
   * 初始化用户数据（菜单和用户信息）
   */
  static async initializeUserData(): Promise<void> {
    try {
      // 获取菜单权限（新接口已包含用户信息）
      const menuResponse = await getMenuPermissionsApi();

      // 处理新的响应格式
      let menuList: Menu.Menu[] = [];
      let userInfo: any = null;

      if (menuResponse && menuResponse.code === 0 && menuResponse.data) {
        const responseData = menuResponse.data;
        
        // 转换菜单数据格式
        if (responseData.menus && Array.isArray(responseData.menus)) {
          menuList = responseData.menus.map(menu => convertNewMenuToOldFormat(menu));
        }
        
        // 保存用户信息
        if (responseData.user) {
          // 处理 roles：如果是字符串数组，转换为对象数组格式以便统一处理
          let processedRoles: any[] = [];
          if (responseData.roles && Array.isArray(responseData.roles)) {
            if (responseData.roles.length > 0) {
              // 如果是字符串数组，转换为对象数组
              if (typeof responseData.roles[0] === 'string') {
                processedRoles = responseData.roles.map((roleKey: string) => {
                  // 角色代码到名称的映射
                  const roleNameMap: Record<string, string> = {
                    'reviewer': '审核者',
                    'assessor': '审核者',
                    'operator': '操作者',
                    'user': '普通用户',
                    'admin': '管理员',
                    'administrator': '管理员',
                    'super_admin': '管理员',
                  };
                  return {
                    role_key: roleKey,
                    role_name: roleNameMap[roleKey.toLowerCase()] || '用户',
                    name: roleNameMap[roleKey.toLowerCase()] || '用户',
                    code: roleKey,
                  };
                });
              } else {
                // 如果已经是对象数组，直接使用
                processedRoles = responseData.roles;
              }
            }
          }
          
          // 合并用户信息，包含新增的 username, mobile, email 字段
          userInfo = {
            ...responseData.user,
            // 兼容 phone 字段（从 mobile 映射）
            phone: responseData.user.mobile || null,
            roles: processedRoles,
            permissions: responseData.permissions || [],
            // 保存部门信息（dept），用于判断用户是否在团队中
            dept: responseData.dept || null
          };
        }
      }

      // 设置菜单列表到全局状态（会自动保存到localStorage）
      if (menuList.length > 0) {
        globalStore.setMenuList(menuList);
      }

      // 设置用户信息到全局状态
      if (userInfo) {
        globalStore.setUserInfo(userInfo);
        // 同时更新 localStorage 中的 'userInfo' 键，确保 UserRoleManager.getCurrentUserRole() 能正确读取
        try {
          localStorage.setItem('userInfo', JSON.stringify(userInfo));
        } catch (error) {
          console.error('保存用户信息到 localStorage 失败:', error);
        }
      }

      // 标记为已初始化
      globalStore.setInitialized(true);

      // 预拉取会员 + 积分快照，避免 AccountMenuPopup / MemberModal 每次打开都重复请求造成闪烁
      try {
        const membershipResponse = await getMembershipCurrentApi();
        if (
          membershipResponse &&
          membershipResponse.code === 0 &&
          membershipResponse.data
        ) {
          globalStore.setMembership(membershipResponse.data);
        }
      } catch (membershipError) {
        // 会员快照获取失败不影响主流程
        console.warn('预拉取会员快照失败:', membershipError);
      }

    } catch (error) {
      console.error('初始化用户数据失败:', error);
      // 即使失败也标记为已初始化，避免重复请求
      globalStore.setInitialized(true);
      throw error;
    }
  }

  /**
   * 清除用户数据
   */
  static clearUserData(): void {
    globalStore.clearAll();
  }

  /**
   * 检查是否已初始化
   */
  static isInitialized(): boolean {
    return globalStore.isInitialized();
  }

  /**
   * 获取菜单列表
   */
  static getMenuList() {
    return globalStore.getMenuList();
  }

  /**
   * 获取用户信息
   */
  static getUserInfo() {
    return globalStore.getUserInfo();
  }
}

